/**
 * PvPReplayScreen Component
 *
 * Displays PvP match battle replays sequentially using BattleScreen.
 * Converts PvP match data format to standard battleState format.
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Trophy, TrendingUp, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { apiGetPvPMatch } from '../services/apiService';
import BattleScreen from './BattleScreen';

const PvPReplayScreen = () => {
  const { setGameState, pvpMatchId, pvpMatchData, battleState, setBattleState } = useGame();
  const { authToken } = useAuth();

  const [matchDetails, setMatchDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBattleIndex, setCurrentBattleIndex] = useState(0);
  const [showingBattle, setShowingBattle] = useState(false);
  const [showMatchResult, setShowMatchResult] = useState(false);
  const [showScoreScreen, setShowScoreScreen] = useState(false);
  const [battleResults, setBattleResults] = useState([null, null, null]); // Track results of all 3 battles

  useEffect(() => {
    const loadMatch = async () => {
      const matchId = pvpMatchId || pvpMatchData?.matchId;
      if (!matchId) {
        setLoading(false);
        return;
      }

      try {
        const details = await apiGetPvPMatch(matchId, authToken);
        setMatchDetails(details);
      } catch (error) {
        console.error('Failed to load match:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMatch();
  }, [pvpMatchId, pvpMatchData, authToken]);

  // Watch for battle completion - don't auto-advance, just show score screen
  useEffect(() => {
    if (showingBattle && battleState?.serverResult) {
      const log = battleState.log || [];
      const logIndex = battleState.logIndex ?? 0;

      // Check if battle is complete
      if (logIndex >= log.length - 1 &&
          (battleState.player?.currentHP <= 0 || battleState.opponent?.currentHP <= 0)) {
        // Don't auto-advance - wait for handleBattleComplete to be called by BattleScreen Continue button
      }
    }
  }, [showingBattle, battleState?.logIndex, battleState?.player?.currentHP, battleState?.opponent?.currentHP]);

  const handleBattleComplete = (winner) => {
    // Record the result of this battle
    // BattleScreen passes battleState.winner which is a string: 'player' or 'opponent'
    // winner === 'player' means YOU won, winner === 'opponent' means opponent won
    console.log('[PvPReplayScreen] handleBattleComplete called - winner:', winner, 'currentBattleIndex:', currentBattleIndex);
    const playerWon = winner === 'player';
    console.log('[PvPReplayScreen] playerWon:', playerWon, 'will record:', playerWon ? 'win' : 'loss');

    const newResults = [...battleResults];
    newResults[currentBattleIndex] = playerWon ? 'win' : 'loss';
    setBattleResults(newResults);

    setShowingBattle(false);
    setBattleState(null);

    // Show score screen after battle completes
    setShowScoreScreen(true);
  };

  const handleContinueToNextBattle = () => {
    setShowScoreScreen(false);

    // Move to next battle or show final result
    if (currentBattleIndex < 2) {
      setCurrentBattleIndex(currentBattleIndex + 1);
      // Battle will start when user is ready
    } else {
      setShowMatchResult(true);
    }
  };

  const startBattle = (battleIndex) => {
    const battle = matchDetails.battles[battleIndex];
    if (!battle) return;

    // Convert PvP battle format to standard battleState format
    // The API already normalized battles so the viewer is always pokemon1
    // pokemon1 is YOUR Pokemon, pokemon2 is OPPONENT's Pokemon

    // Convert numeric winner (1 or 2) to string format ('player' or 'opponent')
    // The API normalized battles so viewer is always pokemon1, so winner===1 means viewer won
    const winnerString = battle.winner === 1 ? 'player' : 'opponent';
    console.log('[PvPReplayScreen] Starting battle', battleIndex, '- battle.winner:', battle.winner, '- winnerString:', winnerString);

    setBattleState({
      serverResult: {
        battleLog: battle.battleLog,
        winner: battle.winner,
        player1: {
          name: battle.pokemon1.name,
          type: battle.pokemon1.type,
          primaryType: Array.isArray(battle.pokemon1.type) ? battle.pokemon1.type[0] : battle.pokemon1.type,
          stats: battle.pokemon1.stats,
          movepool: battle.pokemon1.movepool || [],
          statusEffects: []
        },
        player2: {
          name: battle.pokemon2.name,
          type: battle.pokemon2.type,
          primaryType: Array.isArray(battle.pokemon2.type) ? battle.pokemon2.type[0] : battle.pokemon2.type,
          stats: battle.pokemon2.stats,
          movepool: battle.pokemon2.movepool || [],
          statusEffects: []
        }
      },
      log: battle.battleLog,
      logIndex: 0,
      tick: 0,
      winner: winnerString,  // Set the string winner for BattleScreen to use
      player: {
        name: battle.pokemon1.name,
        primaryType: Array.isArray(battle.pokemon1.type) ? battle.pokemon1.type[0] : battle.pokemon1.type,
        stats: battle.pokemon1.stats,
        currentHP: battle.pokemon1.stats.HP,
        currentStamina: 100,
        statusEffects: [],
        movepool: battle.pokemon1.movepool || []
      },
      opponent: {
        name: battle.pokemon2.name,
        primaryType: Array.isArray(battle.pokemon2.type) ? battle.pokemon2.type[0] : battle.pokemon2.type,
        stats: battle.pokemon2.stats,
        currentHP: battle.pokemon2.stats.HP,
        currentStamina: 100,
        statusEffects: [],
        movepool: battle.pokemon2.movepool || []
      },
      displayLog: [],
      isPvPReplay: true
    });

    setShowingBattle(true);
  };

  // Start battle when ready (called by score screen or initial load)
  useEffect(() => {
    if (matchDetails && !showingBattle && !showMatchResult && !showScoreScreen) {
      // Start the current battle
      startBattle(currentBattleIndex);
    }
  }, [matchDetails, currentBattleIndex, showScoreScreen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <div className="text-pocket-text-light">Loading match...</div>
      </div>
    );
  }

  if (!matchDetails) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <div className="text-pocket-text">Match not found</div>
      </div>
    );
  }

  // Show battle screen
  if (showingBattle && battleState) {
    return <BattleScreen onBattleComplete={handleBattleComplete} />;
  }

  // Show score screen between battles
  if (showScoreScreen) {
    const wins = battleResults.filter(r => r === 'win').length;
    const losses = battleResults.filter(r => r === 'loss').length;
    const battlesCompleted = currentBattleIndex + 1;
    const battlesRemaining = 3 - battlesCompleted;
    const lastBattleResult = battleResults[currentBattleIndex];
    console.log('[PvPReplayScreen] Score screen - battleResults:', battleResults, 'lastBattleResult:', lastBattleResult, 'wins:', wins, 'losses:', losses);

    return (
      <div className="min-h-screen bg-pocket-bg p-4 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-card-lg p-8"
          >
            {/* Battle Result */}
            <div className="text-center mb-6">
              <div className={`text-5xl mb-3 ${lastBattleResult === 'win' ? 'text-green-500' : 'text-red-500'}`}>
                {lastBattleResult === 'win' ? <Trophy className="mx-auto" /> : <TrendingDown className="mx-auto" />}
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${lastBattleResult === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                Battle {battlesCompleted}: {lastBattleResult === 'win' ? 'Victory!' : 'Defeat'}
              </h2>
            </div>

            {/* Current Score */}
            <div className="bg-pocket-bg rounded-xl p-6 mb-6">
              <h3 className="text-center text-pocket-text-light font-semibold mb-4">Current Score</h3>
              <div className="flex justify-around text-center mb-4">
                <div>
                  <div className="text-4xl font-bold text-green-600">{wins}</div>
                  <div className="text-sm text-pocket-text-light">Your Wins</div>
                </div>
                <div className="text-3xl font-bold text-pocket-text-light">-</div>
                <div>
                  <div className="text-4xl font-bold text-red-600">{losses}</div>
                  <div className="text-sm text-pocket-text-light">Losses</div>
                </div>
              </div>

              {/* Battle indicators */}
              <div className="flex justify-center gap-3">
                {[0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${
                      battleResults[index] === 'win'
                        ? 'bg-green-500 text-white'
                        : battleResults[index] === 'loss'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {index + 1}
                  </div>
                ))}
              </div>
            </div>

            {/* Battles Remaining */}
            {battlesRemaining > 0 && (
              <div className="text-center mb-6">
                <p className="text-pocket-text-light">
                  {battlesRemaining} {battlesRemaining === 1 ? 'battle' : 'battles'} remaining
                </p>
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={handleContinueToNextBattle}
              className="w-full px-6 py-3 bg-pocket-red hover:bg-red-600 text-white font-bold rounded-xl transition-colors"
            >
              {battlesRemaining > 0 ? 'Continue to Next Battle' : 'View Final Results'}
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show match result
  if (showMatchResult) {
    const won = matchDetails.winner === 'you';
    const yourData = matchDetails.you || {};
    const opponentData = matchDetails.opponent || {};

    return (
      <div className="min-h-screen bg-pocket-bg p-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-card-lg p-8 text-center"
          >
            <div className={`text-6xl mb-4 ${won ? 'text-green-500' : 'text-red-500'}`}>
              {won ? <Trophy className="mx-auto" /> : <TrendingDown className="mx-auto" />}
            </div>
            <h2 className={`text-3xl font-bold mb-2 ${won ? 'text-green-600' : 'text-red-600'}`}>
              {won ? 'Victory!' : 'Defeat'}
            </h2>
            <p className="text-pocket-text-light mb-6">
              {won ? 'You won the match!' : 'Better luck next time!'}
            </p>

            <div className="bg-pocket-bg rounded-xl p-4 mb-6">
              <div className="flex justify-around text-center">
                <div>
                  <div className="text-2xl font-bold text-pocket-text">
                    {yourData.battlesWon ?? 0}
                  </div>
                  <div className="text-sm text-pocket-text-light">Your Wins</div>
                </div>
                <div className="text-2xl font-bold text-pocket-text-light">-</div>
                <div>
                  <div className="text-2xl font-bold text-pocket-text">
                    {opponentData.battlesWon ?? 0}
                  </div>
                  <div className="text-sm text-pocket-text-light">Opponent Wins</div>
                </div>
              </div>
            </div>

            {yourData.ratingChange !== undefined && yourData.ratingChange !== null && (
              <div className="mb-6">
                <div className={`text-lg font-bold ${yourData.ratingChange > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {yourData.ratingChange > 0 ? <TrendingUp className="inline mr-2" /> : <TrendingDown className="inline mr-2" />}
                  {yourData.ratingChange > 0 ? '+' : ''}{yourData.ratingChange} Rating
                </div>
              </div>
            )}

            <button
              onClick={() => setGameState('pvp')}
              className="w-full px-6 py-3 bg-pocket-red hover:bg-red-600 text-white font-bold rounded-xl transition-colors"
            >
              Back to PvP
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default PvPReplayScreen;
